// Helpers for the spend pacing shown on campaign cards and the campaign
// dashboard. The data comes from dsp-stats-v2 `spend-status`, which only
// evaluates scheduler_mode 1 (internally `evenly`; the UI labels it with the
// display swap in utils/strategy.js).

import i18next from 'i18next';

export const EVENLY_SCHEDULER_MODE = 1;

const DAY_MS = 24 * 60 * 60 * 1000;
const I18N_BASE = 'sections.spend-pacing';

// Same palette as the "Pacing de campañas activas" report: a soft background,
// a colored dot and the text label, so color is never the only encoding.
export const PACING_STATES = {
    behind: {
        labelKey: `${I18N_BASE}.states.behind`,
        dot: '#2a78d6',
        background: '#e3eefb',
    },
    on_track: {
        labelKey: `${I18N_BASE}.states.on_track`,
        dot: '#8b8a85',
        background: '#eeedea',
    },
    overspending: {
        labelKey: `${I18N_BASE}.states.overspending`,
        dot: '#e34948',
        background: '#fbe7e6',
    },
};

export const CORRECTION_STATES = {
    correcting: {
        labelKey: `${I18N_BASE}.correction.correcting`,
        dot: '#0ca30c',
        background: '#e3f4e3',
    },
    diverging: {
        labelKey: `${I18N_BASE}.correction.diverging`,
        dot: '#c77700',
        background: '#fbefd9',
    },
    on_track: {
        labelKey: `${I18N_BASE}.correction.on_track`,
        dot: '#8b8a85',
        background: '#eeedea',
    },
};

export const NOT_APPLICABLE_STYLE = {
    labelKey: `${I18N_BASE}.not-applicable`,
    background: '#eeedea',
};

export const isEvenlyMode = (mode) =>
    mode !== null &&
    mode !== undefined &&
    mode !== '' &&
    Number(mode) === EVENLY_SCHEDULER_MODE;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toTime = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    return new Date(value).getTime();
};

/**
 * Share of the flight already elapsed, counted in closed flight days.
 * The spend-status `daily` block wins when present, so the marker always
 * matches the pacing pill; otherwise it is derived from the campaign dates.
 * @returns {{ day: number, totalDays: number, percent: number } | null}
 */
export const getFlightProgress = ({
    startPeriod,
    endPeriod,
    now = new Date(),
    daily,
} = {}) => {
    const apiTotal = Number(daily?.total);
    const apiElapsed = Number(daily?.elapsed);
    if (apiTotal > 0 && Number.isFinite(apiElapsed)) {
        const day = clamp(apiElapsed, 0, apiTotal);
        return { day, totalDays: apiTotal, percent: (day / apiTotal) * 100 };
    }

    const start = toTime(startPeriod);
    const end = toTime(endPeriod);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return null;
    }

    const totalDays = Math.max(1, Math.round((end - start) / DAY_MS));
    const day = clamp(Math.floor((toTime(now) - start) / DAY_MS), 0, totalDays);
    return { day, totalDays, percent: (day / totalDays) * 100 };
};

const isNumber = (value) =>
    value !== null && value !== undefined && Number.isFinite(Number(value));

// Numbers follow the active UI language (42,0 in es/pt, 42.0 in en).
// `locale` overrides it, mainly for tests.
const formatDecimal = (number, digits, locale) =>
    new Intl.NumberFormat(locale || i18next.language || undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(number);

// Budget points with an explicit sign: +19.4, −4.9, 0.0
export const formatPoints = (value, locale) => {
    if (!isNumber(value)) return '—';
    const number = Number(value);
    const sign = number > 0 ? '+' : number < 0 ? '−' : '';
    return `${sign}${formatDecimal(Math.abs(number), 1, locale)}`;
};

export const formatPercent = (value, locale) =>
    isNumber(value) ? `${formatDecimal(Number(value), 1, locale)}%` : '—';

export const formatRatio = (value, locale) =>
    isNumber(value) ? `${formatDecimal(Number(value), 0, locale)}%` : '—';

// Today's projected spend against the linear daily rate, as a percentage.
